import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  APP_BASE_URL,
  ASSET_ADDITIONAL_INFO,
  ASSET_REPLACEMENT,
  GET_ASSET_FORM,
  GET_DEVICE_TYPE_ASSET,
} from "../../../Common/EndPoints";
import axios from "axios";
import toast from "react-hot-toast";
import { Alert, Button, ButtonGroup } from "@mui/material";
import Loader from "../../Loader/Loader";
import DataTable from "../../DataGrid/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";

const parseReplacementHistory = (data: any) => {
  return data.map((item: any) => ({
    ...item,
    ...(item?.payload ?? {}),
  }));
};

function AssetComponentReplacementHistoryWrapper() {
  const location = useLocation();
  const defaultState = (location?.state as any)?.d;
  const defaultValues = defaultState ?? {};
  const DEVICE_ID = defaultValues?.deviceId;
  return (
    <AssetComponentReplacementHistory DEVICE_ID={DEVICE_ID} gearTypeValue={defaultValues?.gearType} />
  )
}

export function AssetComponentReplacementHistory({ DEVICE_ID, gearTypeValue }: { DEVICE_ID: string, gearTypeValue: string }) {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);


  const [deviceTypeCollectionMapIds, setDeviceTypeCollectionMapIds] = useState<any>(null);

  const [subTabList, setSubTabList] = useState<any[]>([]);
  const [selectedSubTab, setSelectedSubTab] = useState<any>(null);
  const [subFormTabs, setSubFormTabs] = useState<any>(null);
  const [selectedSubFormTab, setSelectedSubFormTab] = useState<any>(null);

  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);

  useEffect(() => {
    if (selectedSubFormTab) {
      const newColumns: GridColDef[] = selectedSubFormTab.sectionFields.map((field: any) => ({
        field: field.key,
        headerName: field.label,
        flex: 1,
        renderCell: (params: any) => {
          return params?.value || "-";
        },
      }));
      newColumns.push({
        field: "dor",
        headerName: "Date of Replacement",
        flex: 1,
        renderCell: (params: any) => {
          return params?.value ? dayjs(params?.value).format("DD-MM-YYYY") : "-";
        },
      });
      setColumns(newColumns);
      getReplacementHistory();
    }
  }, [selectedSubFormTab]);

  const getReplacementHistory = async () => {
    try {
      setTableLoading(true);
      const url = `${APP_BASE_URL}${ASSET_REPLACEMENT}`;
      const params = {
        device_id: DEVICE_ID,
        replacementCompId: selectedSubFormTab.key,
        formId: selectedSubTab.id,
        page: page,
        pageSize: pageSize,
      };
      const res = await axios.get(url, { params });
      const data = res?.data?.data?.content || [];
      setTableData(parseReplacementHistory(data));
      setTotalElements(res?.data?.data?.totalElements ?? 0);
    } catch (error) {
      setTableData([]);
      setTotalElements(0);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    const params = { with_map: true };
    const url = `${APP_BASE_URL}${GET_DEVICE_TYPE_ASSET}`;

    axios.get(url, { params }).then(
      async (res) => {
        const _mapIds: any = {};
        res.data.data.forEach((_deviceType: any) => {
          _mapIds[_deviceType.label] = {
            type_id: _deviceType.typeid,
            collection_map_id: _deviceType?.collectionMaps?.[0]?.collectionmapid,
            type: _deviceType.type,
          };
        });
        setDeviceTypeCollectionMapIds(_mapIds);
        setLoading(false);
      },
      (err) => {
        toast.error("Error Loading Form");
      },
    );
  }, []);

  useEffect(() => {
    if (!gearTypeValue || !deviceTypeCollectionMapIds) return;
    setSubTabData(gearTypeValue);

    function setSubTabData(_collection_map_id: string) {
      const collection_map_id = deviceTypeCollectionMapIds?.[_collection_map_id]?.collection_map_id;
      if (!collection_map_id) {
        setSubTabList([]);
        return;
      }

      axios
        .get(`${APP_BASE_URL}${GET_ASSET_FORM}`, {
          params: { collection_map_id: collection_map_id, publish: true },
        })
        .then((res) => {
          const form_id = res.data.data?.[0]?.id;
          setSubTabList(res.data.data);
          const selectedSubTab = res.data.data?.[0];
          setSelectedSubTab(selectedSubTab);

          if (form_id && DEVICE_ID) {
            const url = `${APP_BASE_URL}${ASSET_ADDITIONAL_INFO}`;
            const params = { device_id: DEVICE_ID, form_id };
            axios.get(url, { params }).then((res) => {
              const formData = res.data.data?.[0]?.data;
              const _subFormTabs = filterReplacementSection(selectedSubTab?.data, formData);

              setSubFormTabs(_subFormTabs);
              const selectedSubFormTab = _subFormTabs?.[0];
              setSelectedSubFormTab(selectedSubFormTab);
            });
          }
        });
    }
  }, [gearTypeValue, deviceTypeCollectionMapIds]);

  const filterReplacementSection = (section: any | undefined, formData: any) => {
    return section && Array.isArray(section)
      ? section?.filter((_subTab: any) => {
        // remove after isReplacement key is added
        if (!_subTab?.isComponentSection) {
          return false;
        }
        if (_subTab.type == "section") {
          return true;
        }
        if (_subTab.type == "reactive_section") {
          const reactiveKey = _subTab.reactiveKey;
          const reactiveKeyValue = formData?.[reactiveKey];
          if (!reactiveKeyValue) {
            return false;
          }

          const checkReactiveValue = (value: string | string[], target: string | string[]): boolean => {
            if (Array.isArray(value)) {
              return Array.isArray(target) ? target.some((t) => value.includes(t)) : value.includes(target);
            } else {
              return Array.isArray(target) ? target.includes(value) : value === target;
            }
          };

          return checkReactiveValue(reactiveKeyValue, _subTab.reactiveValue);
        }
        return false;
      })
      : [];
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="page-container--card">
      {
        subTabList.length > 0 && (
          <>
            <ButtonGroup
              variant="outlined"
              aria-label="outlined button group"
              style={{ display: "block", marginBottom: "1rem" }}
            >
              {subTabList.map((_subTab) => (
                <Button
                  variant={selectedSubTab?.id === _subTab?.id ? "contained" : "outlined"}
                  key={_subTab.id}
                  onClick={() => {
                    setSelectedSubTab(_subTab);
                    const _subFormTabs = filterReplacementSection(_subTab.data?.[0], {});
                    setSubFormTabs(_subFormTabs);
                    setSelectedSubFormTab(_subFormTabs?.[0]);
                  }}
                >
                  {_subTab.name}
                </Button>
              ))}
            </ButtonGroup>
          </>
        )}
      {subFormTabs?.length > 0 && (
        <ButtonGroup variant="text" aria-label="outlined button group" style={{ display: "block", marginBottom: "1rem" }}>
          {subFormTabs?.map((_subForm: any) => (
            <Button
              key={_subForm.key}
              variant={selectedSubFormTab?.key === _subForm?.key ? "contained" : "outlined"}
              onClick={() => {
                setSelectedSubFormTab(_subForm);
              }}
            >
              {_subForm.label}
            </Button>
          ))}
        </ButtonGroup>
      )}

      {columns.length > 0 ? (
        <DataTable
          tableInfo={{
            id: `asset-component-replacement-history`,
            label: `Asset Component Replacement History`,
            requireAuthSign: false,
            timestamp: { startTs: null, endTs: null },
          }}
          rowCount={totalElements}
          loading={tableLoading}
          pageIndex={page}
          pageSize={pageSize}
          pageChange={setPage}
          pageSizeChange={setPageSize}
          rows={tableData}
          columns={columns}
        />
      ) : <Alert severity="error">No Asset Replacement History.</Alert>}
    </div>
  );
}

export default AssetComponentReplacementHistoryWrapper;
